import { logger } from 'src/utils/logger';

/**
 * Generic TTL-bounded cache backed by sessionStorage.
 *
 * Centralizes get/set/invalidate, JSON (de)serialization, TTL expiry, and
 * malformed-entry recovery. sessionStorage is tab-scoped, so entries never
 * leak across tabs or sessions.
 */
export interface CacheEnvelope<T> {
	data: T;
	timestamp: number;
}

export class SessionStorageCache<T> {
	private readonly key: string;
	private readonly ttlMs: number;

	constructor(key: string, ttlMs: number) {
		this.key = key;
		this.ttlMs = ttlMs;
	}

	/**
	 * Returns the cached value if present and within TTL, otherwise null.
	 * Expired or malformed entries are evicted as a side effect.
	 */
	get(): T | null {
		try {
			const raw = sessionStorage.getItem(this.key);
			if (!raw) {
				logger.info(`Cache MISS (${this.key}): no entry found`);
				return null;
			}

			const envelope: CacheEnvelope<T> = JSON.parse(raw);
			const ageMs = Date.now() - envelope.timestamp;

			if (ageMs > this.ttlMs) {
				sessionStorage.removeItem(this.key);
				logger.info(`Cache MISS (${this.key}): expired`, {
					ageMs,
					ttlMs: this.ttlMs,
				});
				return null;
			}

			logger.info(`Cache HIT (${this.key})`, { ageMs });
			return envelope.data;
		} catch {
			sessionStorage.removeItem(this.key);
			logger.info(`Cache MISS (${this.key}): malformed entry removed`);
			return null;
		}
	}

	/**
	 * Milliseconds until the stored entry expires; 0 if absent, expired, or
	 * malformed. Read-only (does not evict), for scheduling a refresh at expiry.
	 */
	msUntilExpiry(): number {
		try {
			const raw = sessionStorage.getItem(this.key);
			if (!raw) {
				return 0;
			}
			const envelope: CacheEnvelope<T> = JSON.parse(raw);
			const remainingMs = this.ttlMs - (Date.now() - envelope.timestamp);
			return remainingMs > 0 ? remainingMs : 0;
		} catch {
			return 0;
		}
	}

	/** Stores a value with the current timestamp. */
	set(data: T): void {
		try {
			const envelope: CacheEnvelope<T> = { data, timestamp: Date.now() };
			sessionStorage.setItem(this.key, JSON.stringify(envelope));
			logger.info(`Cache WRITE (${this.key})`);
		} catch (e) {
			logger.error(`Failed to write cache (${this.key}) to sessionStorage`, {
				error: e,
			});
		}
	}

	/** Removes this cache entry. */
	invalidate(): void {
		sessionStorage.removeItem(this.key);
		logger.info(`Cache INVALIDATED (${this.key})`);
	}

	/**
	 * Removes every sessionStorage entry whose key starts with the given
	 * prefix (for caches that fan out across many keys).
	 */
	static invalidateByPrefix(prefix: string): void {
		const keysToRemove: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key && key.startsWith(prefix)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => sessionStorage.removeItem(key));
		logger.info(`Cache INVALIDATED by prefix (${prefix})`, {
			removed: keysToRemove.length,
		});
	}
}
